import React from 'react';
import { BookOpen, Cross, Bird, Church } from 'lucide-react';
import { Card } from '../components/ds/Card';
import { SectionHeader } from '../components/ds/SectionHeader';
import { PageHero } from '../components/ds/theme';
import prayerBg from '../assets/images/prayer_bg.jpg';
import pastorWilliam from '../assets/images/pastor_william_coleman.avif';

const beliefs = [
  { Icon: BookOpen, title: 'The Bible', desc: 'The inspired, inerrant Word of God — our ultimate authority for faith and practice.' },
  { Icon: Cross, title: 'Salvation Through Christ', desc: "Salvation is a gift of God's grace through faith in Jesus Christ alone." },
  { Icon: Bird, title: 'The Trinity', desc: 'One God eternally existing in three persons — Father, Son, and Holy Spirit.' },
  { Icon: Church, title: 'The Church', desc: 'The body of Christ, called to worship God and reach the world with the Gospel.' },
];

const leaders = [
  { name: 'Pastor William S. Coleman', role: 'Senior Pastor', img: pastorWilliam },
  { name: 'Dr. LaVilla Brevard', role: 'Associate Pastor', img: prayerBg },
];

const timeline = [
  { year: '1885', title: 'Founded', desc: 'Established by a small group of dedicated believers.', tone: 'var(--gradient-brand)' },
  { year: '1978', title: 'New Building', desc: 'As our congregation grew, we moved into our current sanctuary.', tone: 'linear-gradient(135deg, var(--navy-800), var(--navy-600))' },
  { year: '1995', title: 'Youth Ministry', desc: 'We launched a dedicated youth ministry program.', tone: 'linear-gradient(135deg, var(--tan-700), var(--tan-600))' },
  { year: '2010', title: 'Current Era', desc: 'Pastor William S. Coleman became our senior pastor, leading renewed growth.', tone: 'var(--gradient-brand)' },
];

function About() {
  return (
    <main>
      <PageHero eyebrow="Our Story" title="Faith Since 1885" subtitle="Serving Heath Springs with faith, hope, and love." image={prayerBg} />

      <section className="ds-section-y" style={{ background: 'var(--white)', padding: 'var(--section-y-sm) var(--gutter)', textAlign: 'center' }}>
        <div style={{ maxWidth: 'var(--maxw-prose)', margin: '0 auto' }}>
          <SectionHeader eyebrow="Who We Are" title="About Bright Light Baptist Church" />
          <p style={{ fontSize: '18px', lineHeight: 1.8, color: 'var(--text-body)', margin: '24px 0 18px' }}>For over 140 years, Bright Light Baptist Church has been a cornerstone of faith in the Heath Springs community. What began as a small gathering of believers in 1885 has grown into a vibrant, multi-generational church family united by our love for Jesus Christ.</p>
          <p style={{ fontSize: '18px', lineHeight: 1.8, color: 'var(--text-body)', margin: 0 }}>Our journey has been marked by God's faithfulness through every season — and we've witnessed countless lives transformed by the power of the Gospel.</p>
        </div>
      </section>

      <section className="ds-section-y" style={{ background: 'var(--navy-100)', padding: 'var(--section-y-sm) var(--gutter)' }}>
        <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto' }}>
          <SectionHeader title="What We Believe" tone="navy" />
          <div className="ds-grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '26px', marginTop: '48px', maxWidth: '980px', marginInline: 'auto' }}>
            {beliefs.map((b) => (
              <Card key={b.title} accent="navy" padding="36px" style={{ display: 'flex', gap: '22px', alignItems: 'flex-start' }}>
                <div style={{ width: '60px', height: '60px', flex: 'none', borderRadius: 'var(--radius-md)', background: 'var(--navy-100)', color: 'var(--navy-800)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <b.Icon size={28} />
                </div>
                <div>
                  <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '24px', fontWeight: 500, color: 'var(--navy-800)', margin: '0 0 8px' }}>{b.title}</h3>
                  <p style={{ fontSize: '15.5px', color: 'var(--text-body)', lineHeight: 1.7, margin: 0 }}>{b.desc}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="ds-section-y" style={{ background: 'var(--white)', padding: 'var(--section-y-sm) var(--gutter)' }}>
        <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto' }}>
          <SectionHeader title="Our Leadership" />
          <div className="ds-grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '40px', marginTop: '48px', maxWidth: '640px', marginInline: 'auto' }}>
            {leaders.map((l) => (
              <div key={l.name} style={{ textAlign: 'center' }}>
                <div style={{ width: '180px', height: '180px', margin: '0 auto 18px', borderRadius: '50%', overflow: 'hidden', boxShadow: 'var(--shadow-lg)', border: '4px solid var(--cream)' }}>
                  <img src={l.img} alt={l.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '22px', fontWeight: 500, color: 'var(--burgundy-700)', margin: '0 0 4px' }}>{l.name}</h3>
                <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--navy-800)' }}>{l.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ds-section-y" style={{ background: 'var(--cream)', padding: 'var(--section-y-sm) var(--gutter)' }}>
        <div style={{ maxWidth: 'var(--maxw-prose)', margin: '0 auto' }}>
          <SectionHeader title="Our Journey" tone="navy" />
          <div style={{ marginTop: '52px', display: 'grid', gap: '14px' }}>
            {timeline.map((t) => (
              <div key={t.year} style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '32px', alignItems: 'center' }}>
                <div style={{ width: '110px', height: '110px', borderRadius: '50%', background: t.tone, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-serif)', fontSize: '26px', fontWeight: 700, boxShadow: 'var(--shadow-md)' }}>{t.year}</div>
                <div>
                  <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '26px', fontWeight: 500, color: 'var(--burgundy-700)', margin: '0 0 6px' }}>{t.title}</h3>
                  <p style={{ fontSize: '16px', color: 'var(--text-body)', lineHeight: 1.7, margin: 0 }}>{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

export default About;
